<template>
  <div class="w-full bg-white overflow-x-hidden">
    <!-- Hero Section -->
    <section class="min-h-[40vh] relative bg-gradient-to-r from-[#0D0971] to-[#1a1a8e]">
      <div class="absolute inset-0 bg-black/20"></div>
      <div class="relative z-10 h-full flex flex-col justify-center items-center text-center px-6 py-12">
        <h1 class="font-bold text-3xl text-white mb-4">
          {{ categoryData?.title || 'Category Details' }}
        </h1>
        <div class="w-24 h-1 bg-white/50 mb-6"></div>
        <button
          @click="goBack"
          class="mt-4 px-6 py-3 bg-white text-[#0D0971] font-semibold text-sm rounded-lg hover:bg-gray-100 transition-colors duration-300 flex items-center gap-2">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
          </svg>
          {{ t('categoryDetail.backToSourceProduct') }}
        </button>
      </div>
    </section>

    <!-- Content Section -->
    <section class="pt-8 pb-4">
      <div class="px-6">
        <!-- Description -->
        <div class="mb-8">
          <div v-if="Array.isArray(categoryData?.description)" class="space-y-4">
            <p v-for="(paragraph, index) in categoryData.description"
               :key="index"
               class="text-sm text-[#444] leading-relaxed">
              {{ paragraph }}
            </p>
          </div>
          <p v-else class="text-sm text-[#444] leading-relaxed">
            {{ categoryData?.description }}
          </p>
        </div>

        <!-- Sections -->
        <div v-if="categoryData?.sections && categoryData.sections.length > 0">
          <div v-for="(section, sectionIndex) in categoryData.sections" :key="sectionIndex" class="mb-8">
            <!-- Section Title -->
            <h2 class="text-2xl font-bold text-[#0D0971] mb-6 flex items-center gap-2">
              <span class="text-xl">{{ sectionIndex + 1 }} |</span>
              {{ section.title }}
            </h2>

            <!-- Table Display -->
            <div v-if="section.isTable && section.tableData" class="-mx-6 px-6">
              <table class="w-full border-collapse table-fixed">
                <thead>
                  <tr class="bg-[#0D0971] text-white">
                    <th class="text-sm font-bold p-3 text-left border border-[#0D0971] w-[35%]">
                      {{ $i18n.locale === 'vn' ? 'Tên sản phẩm' : 'Product' }}
                    </th>
                    <th class="text-sm font-bold p-3 text-left border border-[#0D0971] w-[65%]">
                      {{ $i18n.locale === 'vn' ? 'Giới thiệu sản phẩm' : 'Overview' }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, rowIndex) in section.tableData" :key="rowIndex"
                      :class="rowIndex % 2 === 0 ? 'bg-[#f3f8ff]' : 'bg-white'">
                    <td class="text-sm font-semibold text-[#0D0971] p-3 border border-gray-300 align-top break-words">
                      {{ row.product }}
                    </td>
                    <td class="text-sm text-[#444] p-3 border border-gray-300 align-top break-words">
                      <div v-if="Array.isArray(row.overview)">
                        <p v-for="(paragraph, pIndex) in row.overview" :key="pIndex" class="mb-2 last:mb-0">
                          {{ paragraph }}
                        </p>
                      </div>
                      <p v-else>{{ row.overview }}</p>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Section Items (Original Display) -->
            <div v-else-if="section.items && section.items.length > 0" class="space-y-4">
              <div v-for="(item, index) in section.items" :key="index"
                   class="bg-gradient-to-br from-[#f3F8FF] to-[#e8f0fe] rounded-lg p-5 border-l-4 border-[#0D0971]">
                <!-- Item Name -->
                <h3 class="text-base font-bold text-[#0D0971] mb-2">{{ item.name }}</h3>

                <!-- Item Description -->
                <p class="text-sm text-[#444] leading-relaxed">
                  {{ item.description }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="bg-[#F3F8FF]">
      <ImageGalleryForMobile :imageItems="filtrationAccessoriesGallery" :imagesPerPage="4" :columns="1" aspectRatio="4 / 3"/>
    </section>
   

    <!-- Category Not Found -->
    <section v-if="!categoryData" class="py-16">
      <div class="px-6 text-center">
        <h2 class="text-2xl font-bold text-[#0D0971] mb-4">{{ t('categoryDetail.categoryNotFound') }}</h2>
        <p class="text-base text-[#666] mb-6">
          {{ t('categoryDetail.categoryNotFoundMessage') }}
        </p>
        <button
          @click="goBack"
          class="px-6 py-3 bg-[#0D0971] text-white font-semibold text-base rounded-lg hover:bg-[#1a1a8e] transition-colors duration-300">
          {{ t('categoryDetail.backToSourceProduct') }}
        </button>
      </div>
    </section>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from 'vue-i18n';
import { pushToDataLayer } from '../utils/gtm';
import { useChatWidget } from '../utils/chatWidget';
import ImageGalleryForMobile from "../components/ImageGalleryForMobile.vue";
import { filtrationAccessoriesGallery } from "../utils/imageData";

const router = useRouter();
const { locale, messages, t } = useI18n();

// Get Filtration Accessories category data from i18n (hardcoded to 'filtration-accessories' category)
const categoryData = computed(() => {
  const categories = messages.value[locale.value]?.sourceProduct?.categorys || [];
  const filtrationCategory = categories.find((cat: any) => cat.id === 'filtration-accessories');
  return filtrationCategory?.detailContent || null;
});

onMounted(() => {
  pushToDataLayer('page_view', {
    page_location: window.location.href,
    page_title: document.title,
    page_referrer: document.referrer,
  });
});

const goBack = () => {
  router.push('/core-offers/source-product');
  pushToDataLayer('click', {
    category: 'MobileCategoryFiltrationAccessoriesDetail',
    label: 'Back to Source Product',
  });
};

const { openChat } = useChatWidget();

const goToContactUs = () => {
  openChat("MobileCategoryFiltrationAccessoriesDetail");
  pushToDataLayer('click', {
    category: 'MobileCategoryFiltrationAccessoriesDetail',
    label: 'Contact Us',
  });
};

// Handle image loading errors
const handleImageError = (event: Event) => {
  const img = event.target as HTMLImageElement;
  img.style.display = 'none';
};

</script>

<style scoped>
/* Add any additional custom styles here */
.inquery {
  position: fixed;
  right: 10px;
  top: 46%;
  z-index: 100;
  width: 5%;
}
</style>

